import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Gift, Loader2 } from 'lucide-react';
import CustomerLayout from '../components/layout/CustomerLayout';
import { useAuth } from '../context/AuthContext';
import { applyContentLocaleToUrl } from '../utils/contentLocaleQuery';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type PublicCfg =
  | { enabled: false; acceptingNewSignups?: false }
  | {
      enabled: true;
      acceptingNewSignups?: true;
      refereeRewardUsd: number;
      referrerRewardUsd: number;
      minQualifyingOrderUsd: number;
    };

type ReferralRewards = {
  refereeUsd: number;
  referrerUsd: number;
  minFirstOrderUsd: number;
};

type ReferralLists = {
  referredBy: { status: string; referrerEmailMasked: string } | null;
  referralsYouMade: Array<{
    id: string;
    status: string;
    createdAt: string;
    qualifiedAt: string | null;
    friendEmailMasked: string | null;
  }>;
};

/** Program inactive; customer has no referral history. */
type MeEndedBare = { enabled: false; programEnded: true };

/** Program inactive; customer still sees history (referrer/referee). */
type MeEndedWithHistory = {
  enabled: false;
  programEnded: true;
  code: string | null;
  shareUrl: null;
  rewards: ReferralRewards;
} & ReferralLists;

type MeActive = {
  enabled: true;
  programEnded: false;
  code: string;
  shareUrl: string;
  rewards: ReferralRewards;
} & ReferralLists;

type MeResp = MeEndedBare | MeEndedWithHistory | MeActive;

function isEndedWithHistory(m: MeResp | null): m is MeEndedWithHistory {
  return (
    m !== null && 'programEnded' in m && m.programEnded === true && 'rewards' in m
  );
}

function isActiveDashboard(m: MeResp | null): m is MeActive {
  return m !== null && m.enabled === true;
}

export default function AccountReferralsPage() {
  const { t } = useTranslation();
  const { authorizedFetch } = useAuth();
  const [pub, setPub] = useState<PublicCfg | null>(null);
  const [me, setMe] = useState<MeResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<null | 'code' | 'link'>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setErr(null);
      try {
        const [pRes, mRes] = await Promise.all([
          fetch(
            applyContentLocaleToUrl(
              `${API_BASE}/referrals/public-config`,
              navigator.language,
            ),
          ),
          authorizedFetch(`${API_BASE}/referrals/me`),
        ]);
        if (cancelled) return;
        if (pRes.ok) setPub((await pRes.json()) as PublicCfg);
        if (mRes.ok) setMe((await mRes.json()) as MeResp);
        else setErr(t('account.referrals_load_error', { defaultValue: 'Could not load your referral data.' }));
      } catch {
        if (!cancelled) setErr(t('account.referrals_load_error', { defaultValue: 'Could not load your referral data.' }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, t]);

  useEffect(
    () => () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    },
    [],
  );

  const flashCopied = (which: 'code' | 'link') => {
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    setCopied(which);
    copyTimerRef.current = setTimeout(() => {
      setCopied(null);
      copyTimerRef.current = null;
    }, 2000);
  };

  const copy = async (text: string, which: 'code' | 'link') => {
    try {
      await navigator.clipboard.writeText(text);
      flashCopied(which);
    } catch {
      /* ignore */
    }
  };

  return (
    <CustomerLayout>
      <div className="max-w-3xl space-y-6">
        <header className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
            <Gift className="h-6 w-6" aria-hidden />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {t('account.referrals_title', { defaultValue: 'Referrals' })}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              {me && isEndedWithHistory(me)
                ? t('account.referrals_subtitle_ended', {
                    defaultValue:
                      'This referral program is no longer open to new participants. Your past activity is shown below.',
                  })
                : t('account.referrals_subtitle', {
                    defaultValue: 'Share your code with friends. Rewards apply when they complete a qualifying first order.',
                  })}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-slate-600">
              {t('account.referrals_signup_only_note', {
                defaultValue:
                  'Referral links and codes are used when someone creates an account—not at checkout. If you signed up with a friend’s link, it is already linked to your profile; you do not enter it again when paying.',
              })}
            </p>
          </div>
        </header>

        {err ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{err}</div>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-16 text-slate-500">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" aria-hidden />
          </div>
        ) : isEndedWithHistory(me) ? (
          <div className="space-y-6">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
              {t('account.referrals_program_ended_banner', {
                defaultValue:
                  'The referral program is no longer open to new participants. Your history below is preserved. Pending referrals that started before the program ended can still complete when qualifying orders are paid.',
              })}
            </div>
            {me.code ? (
              <section className="rounded-xl border border-slate-200 bg-white p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                  {t('account.referrals_your_code', { defaultValue: 'Your invite code' })}
                </h2>
                <p className="mt-2 font-mono text-2xl font-bold tracking-wider text-slate-900">{me.code}</p>
                <p className="mt-2 text-xs text-slate-600">
                  {t('account.referrals_program_ended_no_new_links', {
                    defaultValue: 'New invite links are disabled. This was your code while the program was active.',
                  })}
                </p>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                  <button
                    type="button"
                    onClick={() => void copy(me.code!, 'code')}
                    className={[
                      'inline-flex w-full items-center justify-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors sm:w-auto sm:min-w-[8.5rem]',
                      copied === 'code'
                        ? 'border-slate-500 bg-slate-100 text-slate-900'
                        : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50',
                    ].join(' ')}
                  >
                    {copied === 'code' ? (
                      <Check className="h-3.5 w-3.5 shrink-0" aria-hidden strokeWidth={2.5} />
                    ) : (
                      <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    )}
                    <span aria-live="polite">
                      {copied === 'code'
                        ? t('account.referrals_copied', { defaultValue: 'Copied!' })
                        : t('account.referrals_copy_code', { defaultValue: 'Copy code' })}
                    </span>
                  </button>
                </div>
                <p className="mt-3 text-xs text-slate-600">
                  {t('account.referrals_rewards_blurb', {
                    defaultValue:
                      'Friends get {{ref}} USD; you get {{you}} USD after their first qualifying order (min total {{min}} USD).',
                    ref: me.rewards.refereeUsd.toFixed(2),
                    you: me.rewards.referrerUsd.toFixed(2),
                    min: me.rewards.minFirstOrderUsd.toFixed(2),
                  })}
                </p>
              </section>
            ) : null}

            {me.referredBy ? (
              <section className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
                <h2 className="font-semibold text-slate-900">
                  {t('account.referrals_you_were_referred', { defaultValue: 'You joined with a referral' })}
                </h2>
                <p className="mt-1">
                  {t('account.referrals_referrer_masked', {
                    defaultValue: 'Invited by {{email}} — status: {{status}}',
                    email: me.referredBy.referrerEmailMasked,
                    status:
                      me.referredBy.status === 'QUALIFIED'
                        ? t('account.referrals_status_qualified', { defaultValue: 'Completed' })
                        : me.referredBy.status === 'REJECTED'
                          ? t('account.referrals_status_rejected', { defaultValue: 'Not eligible' })
                          : t('account.referrals_status_pending', { defaultValue: 'Waiting for qualifying order' }),
                  })}
                </p>
                {me.referredBy.status === 'PENDING' ? (
                  <p className="mt-2 text-xs leading-relaxed text-slate-600">
                    {t('account.referrals_pending_hint', {
                      defaultValue:
                        'You do not need anyone to validate this. When your first order is paid and meets the minimum total ({{min}} USD), the referral completes automatically.',
                      min: me.rewards.minFirstOrderUsd.toFixed(2),
                    })}
                  </p>
                ) : null}
                {me.referredBy.status === 'QUALIFIED' ? (
                  <p className="mt-2 text-xs leading-relaxed text-slate-600">
                    {t('account.referrals_qualified_hint', {
                      defaultValue:
                        'Your qualifying first order is complete. Rewards follow the store’s referral program rules.',
                    })}
                  </p>
                ) : null}
                {me.referredBy.status === 'REJECTED' ? (
                  <p className="mt-2 text-xs leading-relaxed text-slate-600">
                    {t('account.referrals_rejected_hint', {
                      defaultValue: 'This referral could not be counted. Contact support if you have questions.',
                    })}
                  </p>
                ) : null}
              </section>
            ) : null}

            <section className="rounded-xl border border-slate-200 bg-white p-4">
              <h2 className="text-sm font-semibold text-slate-900">
                {t('account.referrals_invites_heading', { defaultValue: 'People you invited' })}
              </h2>
              {me.referralsYouMade.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">
                  {t('account.referrals_none_yet', { defaultValue: 'No sign-ups yet. Share your link!' })}
                </p>
              ) : (
                <ul className="mt-3 divide-y divide-slate-100">
                  {me.referralsYouMade.map((r) => (
                    <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                      <span className="text-slate-600">{r.friendEmailMasked ?? '—'}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                        {r.status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : isActiveDashboard(me) ? (
          <div className="space-y-6">
            <section className="rounded-xl border border-emerald-200/70 bg-emerald-50/40 p-5">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-emerald-800">
                {t('account.referrals_your_code', { defaultValue: 'Your invite code' })}
              </h2>
              <p className="mt-2 font-mono text-2xl font-bold tracking-wider text-slate-900">{me.code}</p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <button
                  type="button"
                  onClick={() => void copy(me.code, 'code')}
                  className={[
                    'inline-flex w-full items-center justify-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors sm:w-auto sm:min-w-[8.5rem]',
                    copied === 'code'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                      : 'border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-50',
                  ].join(' ')}
                  aria-label={
                    copied === 'code'
                      ? t('account.referrals_copied', { defaultValue: 'Copied!' })
                      : t('account.referrals_copy_code', { defaultValue: 'Copy code' })
                  }
                >
                  {copied === 'code' ? (
                    <Check className="h-3.5 w-3.5 shrink-0" aria-hidden strokeWidth={2.5} />
                  ) : (
                    <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  )}
                  <span aria-live="polite">
                    {copied === 'code'
                      ? t('account.referrals_copied', { defaultValue: 'Copied!' })
                      : t('account.referrals_copy_code', { defaultValue: 'Copy code' })}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => void copy(me.shareUrl, 'link')}
                  className={[
                    'inline-flex w-full items-center justify-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors sm:w-auto sm:min-w-[10.5rem]',
                    copied === 'link'
                      ? 'border-slate-500 bg-slate-100 text-slate-900'
                      : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50',
                  ].join(' ')}
                  aria-label={
                    copied === 'link'
                      ? t('account.referrals_copied', { defaultValue: 'Copied!' })
                      : t('account.referrals_copy_link', { defaultValue: 'Copy sign-up link' })
                  }
                >
                  {copied === 'link' ? (
                    <Check className="h-3.5 w-3.5 shrink-0" aria-hidden strokeWidth={2.5} />
                  ) : (
                    <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  )}
                  <span aria-live="polite">
                    {copied === 'link'
                      ? t('account.referrals_copied', { defaultValue: 'Copied!' })
                      : t('account.referrals_copy_link', { defaultValue: 'Copy sign-up link' })}
                  </span>
                </button>
              </div>
              <p className="mt-3 text-xs text-slate-600">
                {t('account.referrals_rewards_blurb', {
                  defaultValue:
                    'Friends get {{ref}} USD; you get {{you}} USD after their first qualifying order (min total {{min}} USD).',
                  ref: me.rewards.refereeUsd.toFixed(2),
                  you: me.rewards.referrerUsd.toFixed(2),
                  min: me.rewards.minFirstOrderUsd.toFixed(2),
                })}
              </p>
            </section>

            {me.referredBy ? (
              <section className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
                <h2 className="font-semibold text-slate-900">
                  {t('account.referrals_you_were_referred', { defaultValue: 'You joined with a referral' })}
                </h2>
                <p className="mt-1">
                  {t('account.referrals_referrer_masked', {
                    defaultValue: 'Invited by {{email}} — status: {{status}}',
                    email: me.referredBy.referrerEmailMasked,
                    status:
                      me.referredBy.status === 'QUALIFIED'
                        ? t('account.referrals_status_qualified', { defaultValue: 'Completed' })
                        : me.referredBy.status === 'REJECTED'
                          ? t('account.referrals_status_rejected', { defaultValue: 'Not eligible' })
                          : t('account.referrals_status_pending', { defaultValue: 'Waiting for qualifying order' }),
                  })}
                </p>
                {me.referredBy.status === 'PENDING' ? (
                  <p className="mt-2 text-xs leading-relaxed text-slate-600">
                    {t('account.referrals_pending_hint', {
                      defaultValue:
                        'You do not need anyone to validate this. When your first order is paid and meets the minimum total ({{min}} USD), the referral completes automatically.',
                      min: me.rewards.minFirstOrderUsd.toFixed(2),
                    })}
                  </p>
                ) : null}
                {me.referredBy.status === 'QUALIFIED' ? (
                  <p className="mt-2 text-xs leading-relaxed text-slate-600">
                    {t('account.referrals_qualified_hint', {
                      defaultValue:
                        'Your qualifying first order is complete. Rewards follow the store’s referral program rules.',
                    })}
                  </p>
                ) : null}
                {me.referredBy.status === 'REJECTED' ? (
                  <p className="mt-2 text-xs leading-relaxed text-slate-600">
                    {t('account.referrals_rejected_hint', {
                      defaultValue: 'This referral could not be counted. Contact support if you have questions.',
                    })}
                  </p>
                ) : null}
              </section>
            ) : null}

            <section className="rounded-xl border border-slate-200 bg-white p-4">
              <h2 className="text-sm font-semibold text-slate-900">
                {t('account.referrals_invites_heading', { defaultValue: 'People you invited' })}
              </h2>
              {me.referralsYouMade.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">
                  {t('account.referrals_none_yet', { defaultValue: 'No sign-ups yet. Share your link!' })}
                </p>
              ) : (
                <ul className="mt-3 divide-y divide-slate-100">
                  {me.referralsYouMade.map((r) => (
                    <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                      <span className="text-slate-600">{r.friendEmailMasked ?? '—'}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                        {r.status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : me && 'programEnded' in me && me.programEnded ? (
          <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-600">
            {t('account.referrals_disabled', { defaultValue: 'The referral program is not active right now.' })}
          </p>
        ) : pub && !pub.enabled ? (
          <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-600">
            {t('account.referrals_disabled', { defaultValue: 'The referral program is not active right now.' })}
          </p>
        ) : (
          <p className="text-sm text-slate-500">{t('account.referrals_unavailable', { defaultValue: 'Referrals are unavailable.' })}</p>
        )}
      </div>
    </CustomerLayout>
  );
}
